/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {Grid, LinearProgress} from '@material-ui/core';
import classnames from 'classnames';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {
  DownloadTemplate,
  TemplateColumn,
  TemplateOptions
} from '@ion/core/src/types/templates';
import {IconButton} from '../components/buttons/IconButton';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {FileIcon} from '../icons/FileIcon';
import {TrashCanIcon} from '../icons/TrashCanIcon';
import {dlsColor} from '../theme/theme';
import {DLSTheme} from '../types/types';
import {BulkImportTemplate} from './BulkImportTemplate';
import {ImportParamsType} from './BulkUploadDialog';
import BulkUploadImporter from './BulkUploadImporter';
import ImportProgress from './ImportProgress';

export interface BulkActionProps {
  isBulkAction?: boolean;
  isEmptyFile?: boolean;
  isInvalidFormat?: boolean;
};

export type BulkUploadContainerType = BulkActionProps & {
  id: string;
  uploadTitle: string;
  entity: Entity;
  entityLabel?: string;
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  omitInstructions?: boolean;
  omitDownloadEntityTemplate?: boolean;
  importParams: ImportParamsType;
  templateColumns: TemplateColumn[];
  templateOptions?: TemplateOptions;
  onDownload: (params?: DownloadTemplate) => void;
  handleImport: (files: FileList) => void;
  importStatus: boolean;
  importComplete: boolean;
  clearImport: () => void;
  ignoreEntity?: boolean;
  disabled?: boolean;
  isFormField?: boolean;
  isRequired?: boolean;
};

// defines the styles used by bulk upload dialog ...
const styles = (theme: DLSTheme) => ({
  uploadStatusContainer : {
    marginTop      : '20px',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  hideUploadStatusContainer : {
    display        : 'none',
    marginTop      : '20px',
    justifyContent : 'space-between'
  },
  linearProgress : {
    maxWidth : '150px'
  },
  fileInfoSection : {
    display : 'flex'
  },
  fileIcon : {
    display     : 'flex',
    marginRight : '12px'
  },
  fileSize : {
    opacity : '0.7'
  },
  errorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '16px',
    fontFamily : 'Roboto',
    fontWeight : 400
  }
});

/**
 * Returns a localized string value for the specified value in bytes.
 *
 * ex. const s = formatBytes(10240);   // s = '10 KB'
 *     const t = formatBytes(1048576); // t = '1 MB'
 */
const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (!isUndefined(bytes)) {
    const key = 'common:units.storageSizes';
    const sizes = [
      translate(`${key}.bytes`),
      translate(`${key}.kb`),
      translate(`${key}.mb`),
      translate(`${key}.gb`)
    ];
    if (bytes > 0) {
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }
    return `0 ${sizes[0]}`;
  }
  return '';
};

/**
 * Returns a localized string for the current status field value in the import parameters.
 */
const getLocalizedStatus = (importParams: ImportParamsType): string => {
  let status: string = 'Progress';
  if (importParams.isComplete) {
    status = 'Completed';
  } else if (importParams.isFailure) {
    status = 'Failed';
  }
  return translate(`common:editors.import.upload${status}`);
};

/**
 * The BulkUploadContainer provides the components for users to upload files via file system
 * selection or drag/drop to initiate bulk import.
 *
 * ```typescript
 *
 * import {BulkUploadContainer} from '@ion/dls/src/imports/BulkUploadContainer';
 *
 * <BulkUploadContainer id={id}
 *   size={size}
 *   uploadTitle={uploadTitle}
 *   entity={Entity.asset}
 *   entityLabel={translate('common:entityLabels.devices')}
 *   styleOverrides={overrides}
 *   onClose={onClose}
 *   importParams={importParams}
 *   templateColumns={templateColumns}
 *   templateOptions={templateOptions}
 *   onDownload={handleDownload}
 *   handleImport={handleImport}
 *   importStatus={importStatus}
 *   importComplete={importComplete}
 *   clearImport={clearImport} />
 * ```
 */
export const BulkUploadContainer = ({
  id,
  uploadTitle,
  entity,
  entityLabel,
  styleOverrides,
  omitInstructions,
  omitDownloadEntityTemplate,
  importParams,
  templateColumns,
  templateOptions,
  onDownload,
  handleImport,
  importStatus,
  importComplete,
  clearImport,
  disabled = false,
  ignoreEntity = false,
  isRequired = false,
  isFormField = false,
  isEmptyFile = false,
  isBulkAction = false,
  isInvalidFormat = false
}: BulkUploadContainerType) => {
  const dialogStyles   = useExtendStyles(styles, styleOverrides);
  const classes        = dialogStyles();
  const path           = 'common:editors.import';

  const getImportStatus = useCallback(() => {
    if (isBulkAction && (isEmptyFile || isInvalidFormat)) {
      return false;
    }
    return true;
  }, [isBulkAction, isEmptyFile, isInvalidFormat]);

  return (
    <>
      <BulkUploadImporter id={id}
        isFormField={isFormField}
        isRequired={isRequired}
        disabled={disabled}
        uploadTitle={uploadTitle}
        onImport={handleImport}
        entity={entity}
        entityLabel={entityLabel}
        importParams={importParams}
        omitInstructions={omitInstructions} />
      <BulkImportTemplate entity={entity}
        ignoreEntity={ignoreEntity}
        entityLabel={entityLabel}
        templateColumns={templateColumns}
        templateOptions={templateOptions}
        onDownload={onDownload}
        omitDownloadEntityTemplate={omitDownloadEntityTemplate} />
      {getImportStatus() && (
        <div id='upload-status' className={classnames(importStatus
          ? classes.uploadStatusContainer : classes.hideUploadStatusContainer)}>
          <div className={classes.fileInfoSection}>
            <div className={classes.fileIcon}><FileIcon /></div>
            <div>
              <ImportProgress isCompleted={importComplete}
                text={importParams?.fileName || ''} />
            </div>
          </div>
          <div className={classes.fileSize}>
            <ImportProgress isCompleted={importComplete}
              text={formatBytes(importParams?.fileSize) || ''} />
          </div>
          <div className={classes.linearProgress}>
            <Grid xs item>
              {importStatus ? getLocalizedStatus(importParams) : ''}
            </Grid>
            <Grid xs item>
              {importStatus ? (
                importParams.isComplete ? <LinearProgress variant='determinate' value={100} /> : <LinearProgress />
              ) : ''}
            </Grid>
          </div>
          <IconButton style={{padding: '0px'}} onClick={clearImport} icon={<TrashCanIcon />} />
        </div>
      )}
      {isBulkAction && isInvalidFormat && (
        <span className={classes.errorMessage}>{translate(`${path}.importInvalidMsg`)}</span>
      )}
      {isBulkAction && isEmptyFile && !isInvalidFormat && (
        <span className={classes.errorMessage}>{translate(`${path}.importEmptyMsg`)}</span>
      )}
    </>
  );
};
